import { useEffect, useMemo, useState, useRef, Fragment, type ReactNode } from 'react';
import {
  forensicsApi,
  ApiError,
  type ForensicsResponse,
  type ForensicsParticipant,
  type ForensicsInstance,
  type ForensicsNotification,
  type PipelineTrace,
  type LiveSessions,
} from '@/api/client';
import './ForensicsPage.css';

type Access = 'checking' | 'granted' | 'denied';
type SearchBy = 'auto' | 'correlation' | 'email' | 'subject' | 'meeting';
type Mode = 'search' | 'sentinel';

const CHANNEL_TEAMS = 'TeamsActivityFeed';
const CHANNEL_EMAIL = 'Email';

/** Una fila por persona: cruza participante (elegibilidad) + instancia + notificaciones. */
interface UserRow {
  userId: string;
  name: string;
  email: string | null;
  participant?: ForensicsParticipant;
  instance?: ForensicsInstance;
  teams?: ForensicsNotification;
  emailNotif?: ForensicsNotification;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function isErrorLevel(level: string | null | undefined): boolean {
  const l = (level ?? '').toLowerCase();
  return l === 'error' || l === 'fatal';
}

function buildRows(trace: PipelineTrace, allowlistEmails: Set<string>): UserRow[] {
  const map = new Map<string, UserRow>();
  const ensure = (userId: string): UserRow => {
    let row = map.get(userId);
    if (!row) { row = { userId, name: userId, email: null }; map.set(userId, row); }
    return row;
  };

  for (const p of trace.participants) {
    const row = ensure(p.userId);
    row.participant = p;
    row.email = p.email ?? row.email;
    row.name = p.displayName ?? p.email ?? p.userId;
  }
  for (const inst of trace.surveyInstances) {
    const row = ensure(inst.evaluatorId);
    row.instance = inst;
  }
  for (const n of trace.notifications) {
    const row = ensure(n.recipientId);
    row.email = row.email ?? n.recipientEmail;
    if (!row.participant && n.recipientEmail) row.name = n.recipientEmail;
    if (n.channel === CHANNEL_TEAMS) row.teams = n;
    else if (n.channel === CHANNEL_EMAIL) row.emailNotif = n;
  }

  const rows = Array.from(map.values());
  rows.sort((a, b) => {
    if (a.userId === trace.highlightUserId) return -1;
    if (b.userId === trace.highlightUserId) return 1;
    return a.name.localeCompare(b.name);
  });
  // marcar allowlist implícitamente al render (usa allowlistEmails); nada que ordenar aquí
  void allowlistEmails;
  return rows;
}

function StatusPill({ status }: { status: string }) {
  const s = status.toLowerCase();
  const kind = s === 'sent' || s === 'responded' ? 'ok'
    : s === 'failed' ? 'err'
    : s === 'pending' || s === 'active' ? 'warn'
    : 'muted';
  return <span className={`fx-pill fx-pill--${kind}`}>{status}</span>;
}

function OutcomeDot({ outcome }: { outcome: string }) {
  const kind = outcome === 'Ok' ? 'ok'
    : outcome === 'Error' ? 'err'
    : outcome === 'NotEligible' ? 'warn'
    : 'muted';
  return <span className={`fx-dot fx-dot--${kind}`} title={outcome} />;
}

export function ForensicsPage() {
  const [access, setAccess] = useState<Access>('checking');
  const [mode, setMode] = useState<Mode>('search');
  const [query, setQuery] = useState('');
  const [by, setBy] = useState<SearchBy>('auto');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ForensicsResponse | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(false);

  // Sonda de acceso: el candado UPN vive en el backend. Query vacía ⇒ 403 si no soy yo, 400 si sí.
  useEffect(() => {
    forensicsApi.get('')
      .then(() => setAccess('granted'))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setAccess('denied');
        else setAccess('granted');
      });
  }, []);

  // Si la sesión trae errores en el log crudo (p.ej. de NotificationService), abre el panel solo.
  useEffect(() => {
    const logs = data?.trace.trace?.rawLogs ?? [];
    if (logs.some((l) => isErrorLevel(l.level))) setShowLogs(true);
  }, [data]);

  const allowlistSet = useMemo(() => {
    const set = new Set<string>();
    for (const e of data?.allowlist?.allowedEmails ?? []) set.add(e.toLowerCase());
    return set;
  }, [data]);

  const rows = useMemo(() => {
    if (!data?.trace.trace) return [];
    return buildRows(data.trace.trace, allowlistSet);
  }, [data, allowlistSet]);

  async function runSearch(q: string, kind: SearchBy) {
    if (!q.trim()) return;
    setLoading(true); setError(null); setData(null); setExpanded(null);
    try {
      const res = await forensicsApi.get(q.trim(), kind === 'auto' ? undefined : kind);
      setData(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) { setAccess('denied'); return; }
      setError(e instanceof ApiError ? `Error ${e.status}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Desde el centinela: al terminar (o querer inspeccionar) una sesión, saltar al forense por su
  // CorrelationId.
  function inspectCorrelation(corr: string) {
    setMode('search');
    setBy('correlation');
    setQuery(corr);
    runSearch(corr, 'correlation');
  }

  if (access === 'checking') {
    return <div className="fx-root fx-center"><div className="fx-boot">⌁ inicializando terminal…</div></div>;
  }
  if (access === 'denied') {
    return (
      <div className="fx-root fx-center">
        <div className="fx-denied">
          <div className="fx-denied__glyph">⛔</div>
          <h1>ACCESO DENEGADO</h1>
          <p>Esta terminal no está autorizada para tu identidad.</p>
          <p className="fx-denied__sub">El incidente queda registrado.</p>
        </div>
      </div>
    );
  }

  const trace = data?.trace.trace ?? null;
  const matches = data?.trace.matches ?? [];
  const notFound = data?.trace.notFoundReason ?? null;

  return (
    <div className="fx-root">
      <header className="fx-header">
        <div className="fx-header__title">
          <span className="fx-header__glyph">🔬</span>
          <div>
            <h1>FORENSICS</h1>
            <p>Diagnóstico de sesión · Post-Meeting Pulse</p>
          </div>
        </div>
        <div className="fx-header__right">
          <div className="fx-modes">
            <button
              className={`fx-mode${mode === 'search' ? ' fx-mode--on' : ''}`}
              onClick={() => setMode('search')}
            >🔎 Búsqueda</button>
            <button
              className={`fx-mode${mode === 'sentinel' ? ' fx-mode--on' : ''}`}
              onClick={() => setMode('sentinel')}
            >📡 Centinela</button>
          </div>
          <div className="fx-header__tag">acceso restringido</div>
        </div>
      </header>

      {mode === 'sentinel' ? (
        <Sentinel onInspect={inspectCorrelation} />
      ) : (
      <>
      <section className="fx-search">
        <input
          className="fx-search__input"
          placeholder="CorrelationId · nombre de la sesión · correo…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(query, by); }}
          autoFocus
        />
        <select className="fx-search__by" value={by} onChange={(e) => setBy(e.target.value as SearchBy)}>
          <option value="auto">Auto</option>
          <option value="correlation">CorrelationId</option>
          <option value="subject">Asunto</option>
          <option value="email">Correo</option>
          <option value="meeting">MeetingId</option>
        </select>
        <button className="fx-search__btn" onClick={() => runSearch(query, by)} disabled={loading}>
          {loading ? '◌ rastreando…' : '▸ Diseccionar'}
        </button>
      </section>

      {error && <div className="fx-alert fx-alert--err">⚠ {error}</div>}

      {notFound && (
        <div className="fx-alert fx-alert--warn">
          Sin resultados: <code>{notFound}</code>
        </div>
      )}

      {matches.length > 0 && (
        <div className="fx-panel">
          <h2>Varias sesiones coinciden — elige una</h2>
          <table className="fx-table">
            <thead><tr><th>Asunto</th><th>Estado</th><th>Inicio</th><th>Fin</th><th></th></tr></thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.meetingId}>
                  <td>{m.subject}</td>
                  <td>{m.status}</td>
                  <td>{fmt(m.startedAt)}</td>
                  <td>{fmt(m.endedAt)}</td>
                  <td>
                    <button className="fx-link" onClick={() => runSearch(m.meetingId, 'meeting')}>
                      abrir ▸
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trace && (
        <>
          <SessionSummary trace={trace} allowlistEnabled={data?.allowlist?.enabled ?? false} />
          <StagesTimeline trace={trace} />
          <UserMatrix
            rows={rows}
            trace={trace}
            allowlistSet={allowlistSet}
            allowlistEnabled={data?.allowlist?.enabled ?? false}
            expanded={expanded}
            setExpanded={setExpanded}
          />
          <LeversPanel data={data!} />

          {trace.warnings.length > 0 && (
            <div className="fx-panel">
              <h2>Avisos de la traza</h2>
              <ul className="fx-warnings">
                {trace.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="fx-panel">
            <button className="fx-collapse" onClick={() => setShowLogs((v) => !v)}>
              {showLogs ? '▾' : '▸'} Log crudo ({trace.rawLogs.length})
              {(() => {
                const errs = trace.rawLogs.filter((l) => isErrorLevel(l.level)).length;
                return errs > 0
                  ? <span className="fx-pill fx-pill--err" style={{ marginLeft: 8 }}>{errs} errores</span>
                  : null;
              })()}
            </button>
            {showLogs && (
              <div className="fx-logs">
                {trace.rawLogs.length === 0 && <div className="fx-muted">Sin log crudo para esta sesión.</div>}
                {trace.rawLogs.map((l, i) => (
                  <div key={i} className={`fx-logline fx-logline--${(l.level ?? '').toLowerCase()}`}>
                    <span className="fx-logline__ts">{fmt(l.timestamp)}</span>
                    <span className="fx-logline__svc">{l.service}</span>
                    <span className="fx-logline__lvl">{l.level}</span>
                    <span className="fx-logline__msg">{l.message}{l.exception ? ` · ${l.exception}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {!trace && !loading && !notFound && matches.length === 0 && (
        <div className="fx-empty">
          <div className="fx-empty__glyph">⌗</div>
          <p>Pega un <b>CorrelationId</b>, el <b>nombre de la sesión</b> o un <b>correo</b> y disecciona.</p>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function Sentinel({ onInspect }: { onInspect: (correlationId: string) => void }) {
  const [live, setLive] = useState<LiveSessions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await forensicsApi.live();
        if (!cancelled) { setLive(res); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? `Error ${e.status}` : String(e));
      }
    };
    pull();
    timer.current = setInterval(pull, 12000);
    return () => { cancelled = true; if (timer.current) clearInterval(timer.current); };
  }, [tick]);

  return (
    <div>
      <div className="fx-sentinel-bar">
        <div className="fx-sentinel-live">
          <span className="fx-dot fx-dot--ok fx-pulse" /> EN VIVO
          <span className="fx-muted">· refresca cada 12s</span>
        </div>
        <div className="fx-sentinel-stats">
          <span className="fx-pill fx-pill--ok">{live?.connectedAgentsCount ?? 0} agentes conectados</span>
          <span className="fx-pill fx-pill--warn">{live?.sessions.length ?? 0} sesiones en curso</span>
        </div>
        <button className="fx-link" onClick={() => setTick((t) => t + 1)}>↻ refrescar</button>
      </div>

      {error && <div className="fx-alert fx-alert--err">⚠ {error}</div>}

      {live && live.sessions.length === 0 && (
        <div className="fx-empty">
          <div className="fx-empty__glyph">📡</div>
          <p>Ninguna sesión en curso en este momento.
            {live.connectedAgentsCount > 0
              ? ` Hay ${live.connectedAgentsCount} agente(s) conectado(s) esperando.`
              : ''}
          </p>
        </div>
      )}

      {live?.sessions.map((s) => (
        <div key={s.meetingId} className="fx-panel fx-livecard">
          <div className="fx-livecard__head">
            <div>
              <h2 className="fx-session__subject">{s.subject || '(sin asunto)'}</h2>
              <div className="fx-muted">
                {s.status} · {s.elapsedMinutes != null ? `${s.elapsedMinutes} min en curso` : 'sin hora de inicio'}
                {s.correlationId ? <> · <code>{s.correlationId}</code></> : null}
              </div>
            </div>
            {s.correlationId && (
              <button className="fx-search__btn" onClick={() => onInspect(s.correlationId!)}>
                ▸ Diseccionar
              </button>
            )}
          </div>
          <table className="fx-table">
            <thead><tr><th>Participante</th><th>Agente</th><th>Se unió a tiempo</th></tr></thead>
            <tbody>
              {s.participants.map((p) => (
                <tr key={p.userId}>
                  <td>
                    <div className="fx-user">
                      <span className="fx-user__name">{p.displayName ?? p.email ?? p.userId}</span>
                      <span className="fx-user__email">{p.email ?? ''}</span>
                    </div>
                  </td>
                  <td>
                    {p.agentConnected
                      ? <span className="fx-pill fx-pill--ok"><span className="fx-dot fx-dot--ok fx-pulse" /> conectado</span>
                      : <span className="fx-pill fx-pill--muted">sin agente</span>}
                  </td>
                  <td>{p.joinedEarly
                    ? <span className="fx-pill fx-pill--ok">sí</span>
                    : <span className="fx-pill fx-pill--muted">—</span>}</td>
                </tr>
              ))}
              {s.participants.length === 0 && (
                <tr><td colSpan={3} className="fx-muted">Sin participantes registrados todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {!live && !error && <div className="fx-boot" style={{ padding: 24 }}>⌁ conectando al centinela…</div>}
    </div>
  );
}

function SessionSummary({ trace, allowlistEnabled }: { trace: PipelineTrace; allowlistEnabled: boolean }) {
  const m = trace.meeting;
  const cells: Array<[string, ReactNode]> = [
    ['Estado', m.status],
    ['CorrelationId', <code key="c">{m.correlationId ?? '—'}</code>],
    ['Inicio', fmt(m.startedAt)],
    ['Fin', fmt(m.endedAt)],
    ['Duración', m.durationMinutes != null ? `${Math.round(m.durationMinutes)} min` : '—'],
    ['Recurrente', m.isRecurring ? 'sí' : 'no'],
    ['Asistencia aplicada', fmt(m.attendanceReportAppliedAt)],
    ['Intentos asistencia', String(m.attendanceReportAttempts)],
    ['Match calendario', m.calendarMatchedExactly == null ? '—' : (m.calendarMatchedExactly ? 'exacto' : 'no')],
    ['Allowlist', allowlistEnabled ? <span key="a" className="fx-pill fx-pill--warn">ACTIVA</span> : <span key="a" className="fx-pill fx-pill--ok">sin filtro</span>],
  ];
  return (
    <div className="fx-panel">
      <h2 className="fx-session__subject">{m.subject || '(sin asunto)'}</h2>
      <div className="fx-grid">
        {cells.map(([k, v]) => (
          <div key={k} className="fx-grid__cell">
            <div className="fx-grid__k">{k}</div>
            <div className="fx-grid__v">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StagesTimeline({ trace }: { trace: PipelineTrace }) {
  const labels: Record<string, string> = {
    MeetingEnd: 'Fin de reunión',
    MeetingEligibility: 'Elegibilidad reunión',
    ParticipantEligibility: 'Elegibilidad participante',
    SurveyCreation: 'Creación de encuesta',
    Notification: 'Notificación',
  };
  return (
    <div className="fx-panel">
      <h2>Pipeline</h2>
      <div className="fx-stages">
        {trace.stages.map((s) => (
          <div key={s.order} className="fx-stage">
            <OutcomeDot outcome={s.outcome} />
            <div className="fx-stage__body">
              <div className="fx-stage__name">{labels[s.name] ?? s.name}</div>
              <div className="fx-stage__summary">{s.summary}</div>
              {s.details.length > 0 && (
                <ul className="fx-stage__details">
                  {s.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function allowlistVerdict(email: string | null, set: Set<string>, enabled: boolean): { label: string; kind: string } {
  if (!enabled) return { label: 'sin filtro', kind: 'muted' };
  if (!email) return { label: '¿?', kind: 'warn' };
  return set.has(email.toLowerCase())
    ? { label: 'permitido', kind: 'ok' }
    : { label: 'BLOQUEADO', kind: 'err' };
}

function UserMatrix({
  rows, trace, allowlistSet, allowlistEnabled, expanded, setExpanded,
}: {
  rows: UserRow[]; trace: PipelineTrace; allowlistSet: Set<string>; allowlistEnabled: boolean;
  expanded: string | null; setExpanded: (id: string | null) => void;
}) {
  return (
    <div className="fx-panel">
      <h2>Por usuario ({rows.length})</h2>
      <table className="fx-table fx-matrix">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Elegible</th>
            <th>Muestra</th>
            <th>Encuesta</th>
            <th>Teams</th>
            <th>Email</th>
            <th>Allowlist</th>
            <th>Agente</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const av = allowlistVerdict(r.email, allowlistSet, allowlistEnabled);
            const isOpen = expanded === r.userId;
            const isHi = r.userId === trace.highlightUserId;
            return (
              <Fragment key={r.userId}>
                <tr className={`fx-matrix__row${isHi ? ' fx-matrix__row--hi' : ''}`}>
                  <td>
                    <div className="fx-user">
                      <span className="fx-user__name">{r.name}</span>
                      <span className="fx-user__email">{r.email ?? shortId(r.userId)}</span>
                    </div>
                  </td>
                  <td>{r.participant
                    ? (r.participant.isEligible ? <span className="fx-pill fx-pill--ok">sí</span> : <span className="fx-pill fx-pill--err">no</span>)
                    : <span className="fx-muted">n/d</span>}</td>
                  <td>{r.participant
                    ? (r.participant.includedInSample ? <span className="fx-pill fx-pill--ok">sí</span> : <span className="fx-pill fx-pill--muted">no</span>)
                    : <span className="fx-muted">n/d</span>}</td>
                  <td>{r.instance ? <StatusPill status={r.instance.status} /> : <span className="fx-muted">—</span>}</td>
                  <td>{r.teams ? <StatusPill status={r.teams.status} /> : <span className="fx-muted">—</span>}</td>
                  <td>{r.emailNotif ? <StatusPill status={r.emailNotif.status} /> : <span className="fx-muted">—</span>}</td>
                  <td><span className={`fx-pill fx-pill--${av.kind}`}>{av.label}</span></td>
                  <td><span className="fx-muted" title="Histórico de presencia del agente: Fase 2">n/d</span></td>
                  <td>
                    <button className="fx-link" onClick={() => setExpanded(isOpen ? null : r.userId)}>
                      {isOpen ? '▾' : '▸'}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="fx-matrix__detail">
                    <td colSpan={9}>
                      <UserDetail row={r} verdict={av} allowlistEnabled={allowlistEnabled} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="fx-muted">Sin participantes ni notificaciones para esta sesión.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UserDetail({ row, verdict, allowlistEnabled }: { row: UserRow; verdict: { label: string; kind: string }; allowlistEnabled: boolean }) {
  const p = row.participant;
  const inst = row.instance;
  return (
    <div className="fx-detail">
      <div className="fx-detail__col">
        <h4>¿Le tocaba encuesta?</h4>
        {p ? (
          <ul>
            <li>Elegible: <b>{p.isEligible ? 'sí' : 'no'}</b>{p.reason ? ` — ${p.reason}` : ''}</li>
            <li>En muestra: <b>{p.includedInSample ? 'sí' : 'no'}</b>{p.samplingReason ? ` — ${p.samplingReason}` : ''}</li>
            <li>Encuestas hoy / semana: {p.surveysToday} / {p.surveysThisWeek}</li>
            <li>Horas desde última reunión elegible: {p.hoursSinceLastEligibleMeeting ?? '—'}</li>
            <li>OU: {p.ouId ?? '—'} · TZ: {p.timezone}</li>
          </ul>
        ) : (
          <p className="fx-muted">Sin detalle de elegibilidad (el registro de pipeline caduca a los 30 días).</p>
        )}
      </div>
      <div className="fx-detail__col">
        <h4>Entrega</h4>
        <ul>
          <li>Encuesta: {inst ? `${inst.status} · creada ${fmt(inst.createdAt)} · expira ${fmt(inst.expiresAt)}${inst.respondedAt ? ` · respondió ${fmt(inst.respondedAt)}` : ''}` : '— (sin instancia)'}</li>
          <li>Teams: {row.teams ? `${row.teams.status}${row.teams.errorMessage ? ` — ${row.teams.errorMessage}` : ''}` : '— (sin registro)'}</li>
          <li>Email: {row.emailNotif ? `${row.emailNotif.status}${row.emailNotif.errorMessage ? ` — ${row.emailNotif.errorMessage}` : ''}` : '— (sin registro)'}</li>
          <li>Allowlist: <span className={`fx-pill fx-pill--${verdict.kind}`}>{verdict.label}</span>
            {allowlistEnabled && verdict.kind === 'err' && !row.teams && !row.emailNotif && (
              <em className="fx-hint"> ← explica que no haya ningún registro de envío</em>
            )}
          </li>
          <li>Agente: <span className="fx-muted">histórico no disponible (Fase 2: persistir conexiones)</span></li>
        </ul>
      </div>
    </div>
  );
}

function LeversPanel({ data }: { data: ForensicsResponse }) {
  const KEY = new Set([
    'MEETING_MAX_PARTICIPANTS', 'MAX_EVALUATIONS_PER_DAY', 'MAX_EVALUATIONS_PER_WEEK',
    'SURVEY_MIN_INTERVAL_HOURS', 'MEETING_MIN_DURATION_MINUTES', 'MEETING_MIN_PARTICIPANTS',
    'EXCLUDE_RECURRING_MEETINGS', 'EXCLUDE_KEYWORDS', 'EXCLUDE_OUTSIDE_WORK_HOURS', 'OU_ACTIVE',
  ]);
  const rules = [...data.rules].sort((a, b) => {
    const ak = KEY.has(a.code) ? 0 : 1, bk = KEY.has(b.code) ? 0 : 1;
    return ak - bk || a.code.localeCompare(b.code);
  });
  return (
    <div className="fx-panel">
      <h2>Palancas de configuración</h2>
      {data.allowlist && (
        <div className={`fx-lever fx-lever--allowlist ${data.allowlist.enabled ? 'fx-lever--on' : ''}`}>
          <div className="fx-lever__head">
            <b>RecipientAllowlist</b>
            <span className={`fx-pill fx-pill--${data.allowlist.enabled ? 'warn' : 'ok'}`}>
              {data.allowlist.enabled ? 'ACTIVA' : 'inactiva'}
            </span>
          </div>
          {data.allowlist.enabled && (
            <div className="fx-lever__emails">
              {data.allowlist.allowedEmails.map((e) => <code key={e}>{e}</code>)}
            </div>
          )}
        </div>
      )}
      <table className="fx-table fx-levers">
        <thead><tr><th>Regla</th><th>Valor</th><th>Default</th><th>Activa</th><th>Descripción</th></tr></thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.code} className={KEY.has(r.code) ? 'fx-levers__key' : ''}>
              <td><code>{r.code}</code></td>
              <td><b>{r.value}</b></td>
              <td className="fx-muted">{r.defaultValue}</td>
              <td>{r.isActive ? <span className="fx-pill fx-pill--ok">sí</span> : <span className="fx-pill fx-pill--muted">no</span>}</td>
              <td className="fx-levers__desc">{r.description}</td>
            </tr>
          ))}
          {rules.length === 0 && <tr><td colSpan={5} className="fx-muted">RulesEngine no respondió.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
